import { ReviewerStatus, SignatureStatus, SubmissionStatus } from "generated-graphql/graphql";
import { useCallback, useEffect, useState } from "react";

export type SignatureConfirmationState = "idle" | "pending" | "delayed";

const SIGNATURE_CONFIRMATION_POLL_INTERVAL_MS = 2_000;
const SIGNATURE_CONFIRMATION_TIMEOUT_MS = 30_000;

type SignatureReviewer = {
  isSigner?: boolean | null;
  completedAt?: string | null;
  reviewerStatus?: ReviewerStatus | null;
  signatureStatus?: SignatureStatus | null;
  user: {
    id: string;
  };
};

export function useSignatureConfirmation({
  currentUserId,
  reviewers,
  submissionStatus,
  refetchSubmission,
}: {
  currentUserId?: string;
  reviewers: SignatureReviewer[];
  submissionStatus?: SubmissionStatus | null;
  refetchSubmission: () => Promise<unknown>;
}) {
  const [confirmationState, setConfirmationState] = useState<SignatureConfirmationState>("idle");

  // BoldSign's iframe event only confirms that the provider accepted the
  // signature. Completion is authoritative only after its webhook updates the
  // reviewer row and that persisted state returns through the submission query.
  const currentSigner = reviewers.find(
    (reviewer) => reviewer.isSigner && reviewer.user.id === currentUserId
  );
  const signingCompleted = currentSigner?.signatureStatus === SignatureStatus.Completed;
  const allReviewsCompletedAndApproved =
    reviewers.length > 0 &&
    reviewers.every(
      (reviewer) =>
        reviewer.completedAt != null && reviewer.reviewerStatus === ReviewerStatus.Approved
    );
  const isAwaitingPostSignatureTransition =
    signingCompleted &&
    submissionStatus === SubmissionStatus.InReview &&
    allReviewsCompletedAndApproved;
  const signingFlowConfirmed = signingCompleted && !isAwaitingPostSignatureTransition;
  const activeConfirmationState =
    confirmationState === "idle" && isAwaitingPostSignatureTransition
      ? "pending"
      : confirmationState;

  useEffect(() => {
    if (activeConfirmationState !== "pending" || signingFlowConfirmed) {
      return;
    }

    const refresh = async () => {
      try {
        await refetchSubmission();
      } catch {
        // Keep polling until the confirmation window expires. The delayed
        // state gives the user an explicit retry if the API remains unavailable.
      }
    };

    void refresh();
    const pollInterval = window.setInterval(() => {
      void refresh();
    }, SIGNATURE_CONFIRMATION_POLL_INTERVAL_MS);
    const confirmationTimeout = window.setTimeout(() => {
      setConfirmationState("delayed");
    }, SIGNATURE_CONFIRMATION_TIMEOUT_MS);

    return () => {
      window.clearInterval(pollInterval);
      window.clearTimeout(confirmationTimeout);
    };
  }, [activeConfirmationState, refetchSubmission, signingFlowConfirmed]);

  const markSignatureSubmitted = useCallback(() => {
    setConfirmationState("pending");
  }, []);

  const retrySignatureConfirmation = useCallback(() => {
    setConfirmationState("pending");
  }, []);

  return {
    signingCompleted,
    signingFlowConfirmed,
    signingConfirmationPending: activeConfirmationState !== "idle" && !signingFlowConfirmed,
    signatureConfirmationState: activeConfirmationState,
    isAwaitingPostSignatureTransition,
    markSignatureSubmitted,
    retrySignatureConfirmation,
  };
}
